import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { Pager, STATUS_BADGE, label, todayStr, fmtTime } from "@/components/Pager";
import { AttachmentUpload, AttachmentList } from "@/components/Attachments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BookOpenText, Pencil, Trash2, Send, Eye, Check, X, MessageSquare, Plus } from "lucide-react";

const ALL = "__all__";
const FIELDS = [["topics", "Today's teaching topics *"], ["objectives", "Learning objectives"], ["activities", "Classroom activities"], ["homework", "Homework"],
  ["worksheets", "Worksheets"], ["announcements", "Class-wide announcements"], ["teacher_notes", "Teacher notes (not shown to parents)"]];
const blank = () => ({ date: todayStr(), subject: "", topics: "", objectives: "", activities: "", homework: "", worksheets: "", announcements: "", teacher_notes: "", student_remarks: [], attachments: [] });

function StudentRemarks({ cls, value, onChange }) {
  const [students, setStudents] = useState([]);
  const [pick, setPick] = useState({ student_id: "", remark: "" });
  useEffect(() => {
    if (!cls.branch_id || !cls.class_name || !cls.section) { setStudents([]); return; }
    api.get("/diary/students", { params: cls }).then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, [cls.branch_id, cls.class_name, cls.section]);
  function add() {
    const s = students.find((x) => x.id === pick.student_id);
    onChange([...value.filter((x) => x.student_id !== s.id), { student_id: s.id, student_name: s.name, remark: pick.remark }]);
    setPick({ student_id: "", remark: "" });
  }
  return (
    <div className="space-y-2">
      <Label>Student individual remarks (each parent sees only their child's)</Label>
      <div className="flex flex-wrap gap-2">
        <Select value={pick.student_id} onValueChange={(v) => setPick({ ...pick, student_id: v })}>
          <SelectTrigger data-testid="diary-remark-student" className="w-52"><SelectValue placeholder={students.length ? "Choose student" : "No students in class"} /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="diary-remark-text" placeholder="Remark" value={pick.remark} onChange={(e) => setPick({ ...pick, remark: e.target.value })} className="flex-1 min-w-[180px]" />
        <Button type="button" variant="outline" data-testid="diary-remark-add" disabled={!pick.student_id || !pick.remark} onClick={add}><Plus size={14} /></Button>
      </div>
      {value.map((r) => (
        <div key={r.student_id} className="text-sm flex items-center gap-2 bg-violet-50 rounded-lg px-3 py-1.5">
          <b>{r.student_name}:</b> <span className="flex-1">{r.remark}</span>
          <button type="button" onClick={() => onChange(value.filter((x) => x.student_id !== r.student_id))} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function DiaryForm({ user, editing, onDone }) {
  const isTeacher = user.role === "teacher";
  const [classes, setClasses] = useState([]);
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isTeacher) api.get("/teacher-assignments").then((r) => { setClasses(r.data); if (r.data[0] && !editing) setCls({ branch_id: user.branch_id, class_name: r.data[0].class_name, section: r.data[0].section }); }).catch(() => {});
  }, [isTeacher]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editing) { setF({ ...blank(), ...editing }); setCls({ branch_id: editing.branch_id, class_name: editing.class_name, section: editing.section }); } else setF(blank());
  }, [editing]);

  async function save(publish) {
    setBusy(true);
    try {
      const body = { ...cls, ...Object.fromEntries(["date", "subject", ...FIELDS.map((x) => x[0]), "student_remarks", "attachments"].map((k) => [k, f[k]])) };
      const { data } = editing ? await api.put(`/diaries/${editing.id}`, body) : await api.post("/diaries", body);
      if (publish) {
        const { data: p } = await api.post(`/diaries/${data.id}/publish`);
        toast.success(p.status === "published" ? "Published to parents" : "Sent to Principal for approval");
      } else toast.success("Draft saved");
      setF(blank()); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }
  const ok = cls.class_name && cls.section && f.subject && f.topics && f.date;
  return (
    <div data-testid="diary-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <h3 className="font-display font-bold text-slate-900">{editing ? "Edit diary" : "New class diary"}</h3>
      {isTeacher ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Class & section</Label>
            <Select value={cls.class_name ? `${cls.class_name}|${cls.section}` : ""} onValueChange={(v) => { const [c, s] = v.split("|"); setCls({ branch_id: user.branch_id, class_name: c, section: s }); }}>
              <SelectTrigger data-testid="diary-class" className="mt-1"><SelectValue placeholder={classes.length ? "Choose class" : "No class assigned yet"} /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={`${c.class_name}|${c.section}`}>{c.class_name} - {c.section}{c.subject ? ` (${c.subject})` : ""}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Date</Label><Input data-testid="diary-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1" /></div>
          <div><Label>Subject *</Label><Input data-testid="diary-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1" /></div>
        </div>
      ) : (
        <>
          <ClassPicker value={cls} onChange={setCls} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Date</Label><Input data-testid="diary-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Subject *</Label><Input data-testid="diary-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1" /></div>
          </div>
        </>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label><Textarea data-testid={`diary-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" rows={2} /></div>)}
      </div>
      <StudentRemarks cls={cls} value={f.student_remarks} onChange={(v) => setF({ ...f, student_remarks: v })} />
      <div><Label>Photos & documents</Label><div className="mt-1"><AttachmentUpload value={f.attachments} onChange={(v) => setF({ ...f, attachments: v })} testid="diary-attach" /></div></div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button data-testid="diary-save-draft" variant="outline" disabled={!ok || busy} onClick={() => save(false)}>Save Draft</Button>
        <Button data-testid="diary-publish" disabled={!ok || busy} onClick={() => save(true)} className="btn-brand"><Send size={15} className="mr-1" /> Publish to Parents</Button>
        {editing && <Button variant="ghost" onClick={() => onDone(true)} data-testid="diary-cancel-edit">Cancel</Button>}
      </div>
    </div>
  );
}

function AckPanel({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/diaries/${id}/acks`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, [id]);
  if (!d) return null;
  return (
    <div data-testid={`diary-acks-${id}`} className="border-t pt-3 text-sm grid sm:grid-cols-2 gap-3">
      <div><div className="font-semibold text-emerald-700 mb-1">Seen ({d.acks.length}/{d.total_parents})</div>
        {d.acks.length === 0 ? <p className="text-slate-500 text-xs">No parent has seen this yet.</p> : d.acks.map((a) => <div key={a.id} className="text-xs">{a.parent_name}{a.student_name ? ` (${a.student_name})` : ""} • {fmtTime(a.seen_at)}</div>)}</div>
      <div><div className="font-semibold text-rose-700 mb-1">Not seen ({d.pending.length})</div>{d.pending.map((p) => <div key={p.id} className="text-xs">{p.name}</div>)}</div>
    </div>
  );
}

function DiaryCard({ d, user, onEdit, reload }) {
  const isManager = user.role !== "teacher";
  const canReview = d.status === "pending_approval" || (d.status === "pending_principal" && user.role !== "coordinator");
  const mine = d.teacher_id === user.id;
  const [acks, setAcks] = useState(false);
  const [remark, setRemark] = useState("");
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); setRemark(""); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div data-testid={`diary-${d.id}`} className="bg-white rounded-2xl shadow-sm p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display font-bold text-slate-900">{d.subject}</span>
        <Badge variant="outline">{d.class_name} - {d.section}</Badge>
        <Badge data-testid={`diary-status-${d.id}`} className={`${STATUS_BADGE[d.status]} capitalize`}>{label(d.status)}</Badge>
        {d.auto_approved && <Badge className="bg-sky-100 text-sky-800">Auto-approved</Badge>}
        <span className="text-xs text-slate-500">{fmtDate(d.date)} • {d.teacher_name}{d.status === "published" ? ` • ${d.ack_count || 0} seen` : ""}</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {(mine || isManager) && <Button size="sm" variant="outline" data-testid={`diary-edit-${d.id}`} onClick={() => onEdit(d)}><Pencil size={14} className="mr-1" /> Edit</Button>}
          {mine && ["draft", "rejected"].includes(d.status) && <Button size="sm" data-testid={`diary-publish-${d.id}`} onClick={() => act(() => api.post(`/diaries/${d.id}/publish`), "Submitted")} className="btn-brand"><Send size={14} className="mr-1" /> Publish</Button>}
          {d.status === "published" && <Button size="sm" variant="outline" data-testid={`diary-view-acks-${d.id}`} onClick={() => setAcks(!acks)}><Eye size={14} className="mr-1" /> Parent acknowledgments</Button>}
          {((mine && d.status !== "published") || isManager) && <Button size="sm" variant="outline" data-testid={`diary-delete-${d.id}`} onClick={() => window.confirm("Delete this diary?") && act(() => api.delete(`/diaries/${d.id}`), "Deleted")} className="text-rose-600"><Trash2 size={14} /></Button>}
        </div>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-line"><b>Topics:</b> {d.topics}</p>
      {d.homework && <p className="text-sm text-slate-700 whitespace-pre-line"><b>Homework:</b> {d.homework}</p>}
      <AttachmentList items={d.attachments} />
      {d.review_remark && <p className="text-sm bg-amber-50 rounded-xl p-2.5"><b>Review note:</b> {d.review_remark}</p>}
      {d.coordinator_approved_by && <p className="text-xs text-slate-500">Coordinator approval: {d.coordinator_approved_by} • {fmtTime(d.coordinator_approved_at)}</p>}
      {(d.principal_remarks || []).map((r, i) => <p key={i} className="text-sm bg-violet-50 rounded-xl p-2.5"><b>{r.by}:</b> {r.remark} <span className="text-xs text-slate-500">{fmtTime(r.at)}</span></p>)}
      {isManager && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Input data-testid={`diary-principal-remark-${d.id}`} placeholder={d.status === "pending_principal" ? "Final approval (optional remark)" : d.status === "pending_approval" ? "Remark / reason (optional)" : "Add principal remark"} value={remark} onChange={(e) => setRemark(e.target.value)} className="h-9 flex-1 min-w-[180px]" />
          {canReview ? (<>
            <Button size="sm" data-testid={`diary-approve-${d.id}`} onClick={() => act(() => api.post(`/diaries/${d.id}/review`, { status: "approved", remark }), user.role === "coordinator" ? "Approved — sent to Principal" : "Approved & published")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" data-testid={`diary-reject-${d.id}`} onClick={() => act(() => api.post(`/diaries/${d.id}/review`, { status: "rejected", remark }), "Rejected")} className="text-rose-600"><X size={14} className="mr-1" /> Reject</Button>
          </>) : d.status === "pending_principal" ? <span className="text-xs text-orange-700 self-center">Waiting for Principal's final approval</span> : <Button size="sm" variant="outline" disabled={!remark} data-testid={`diary-remark-save-${d.id}`} onClick={() => act(() => api.post(`/diaries/${d.id}/remark`, { remark }), "Remark added")}><MessageSquare size={14} /></Button>}
        </div>
      )}
      {acks && <AckPanel id={d.id} />}
    </div>
  );
}

export default function ClassDiaryPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState(null);
  const [flt, setFlt] = useState({ day: "", status: params.get("status") || ALL, q: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const load = () => {
    const p = { page, q: flt.q };
    if (flt.day) p.day = flt.day;
    if (flt.status !== ALL) p.status = flt.status;
    return api.get("/diaries", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [flt, page]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => { setFlt({ ...flt, [k]: v }); setPage(1); };
  return (
    <AppShell>
      <PageHeader eyebrow="Daily diary" title="Class Diary" subtitle="Write the day's topics, homework and remarks, then publish to parents." />
      <DiaryForm user={user} editing={editing} onDone={() => { setEditing(null); load(); }} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Input data-testid="diary-filter-date" type="date" value={flt.day} onChange={(e) => set("day", e.target.value)} className="w-40 bg-white" />
        <Select value={flt.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="diary-filter-status" className="w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{["draft", "pending_approval", "pending_principal", "published", "rejected"].map((x) => <SelectItem key={x} value={x}>{label(x)}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="diary-filter-search" placeholder="Search subject, topic, teacher" value={flt.q} onChange={(e) => set("q", e.target.value)} className="w-56 bg-white" />
        {flt.day && <Button variant="ghost" onClick={() => set("day", "")} data-testid="diary-filter-clear">Clear date</Button>}
      </div>
      {data.items.length === 0 ? <Empty testid="diary-empty" icon={BookOpenText} text="No diaries found." /> : (
        <div className="space-y-3">{data.items.map((d) => <DiaryCard key={d.id} d={d} user={user} onEdit={(x) => { setEditing(x); window.scrollTo({ top: 0, behavior: "smooth" }); }} reload={load} />)}</div>
      )}
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="diary-pager" />
    </AppShell>
  );
}
